export type RiderHistoryPhase = {
	rank?: number;
	points?: number;
	time?: string;
	score?: number;
	split1?: string;
	split2?: string;
};

export type RiderHistoryItem = {
	_id?: string;
	event?: {
		name?: string;
		location?: string;
		round?: number;
		date?: string;
	};
	event_date?: string;
	sport?: {
		name?: string;
	};
	series?: {
		name?: string;
	};
	season?: {
		_id?: string;
		name?: string;
		startDate?: string;
		endDate?: string;
	};
	qualifying_data?: RiderHistoryPhase;
	semis_data?: RiderHistoryPhase;
	finals_data?: RiderHistoryPhase;
	total_points?: number;
};

export type SeasonOption = {
	key: string;
	label: string;
	lastResultDate: number;
};

export const hasMeaningfulPhaseData = (phase?: RiderHistoryPhase) => {
	if (!phase) {
		return false;
	}

	if ((phase.rank ?? 0) > 0) {
		return true;
	}

	if ((phase.points ?? 0) > 0) {
		return true;
	}

	if ((phase.score ?? 0) > 0) {
		return true;
	}

	if (typeof phase.time === "string" && phase.time.trim() !== "") {
		return true;
	}

	if (typeof phase.split1 === "string" && phase.split1.trim() !== "") {
		return true;
	}

	if (typeof phase.split2 === "string" && phase.split2.trim() !== "") {
		return true;
	}

	return false;
};

export const formatDateValue = (value?: string) => {
	if (!value) {
		return "-";
	}

	const parsedDate = new Date(value);
	if (Number.isNaN(parsedDate.getTime())) {
		return "-";
	}

	return parsedDate.toLocaleDateString();
};

export const getSeasonKey = (item: RiderHistoryItem) => {
	if (item?.season?._id) {
		return `season-${item.season._id}`;
	}

	const fallbackDate = item.event_date || item.event?.date;
	const parsedDate = fallbackDate ? new Date(fallbackDate) : null;
	if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
		return `year-${parsedDate.getFullYear()}`;
	}

	return "unassigned";
};

export const buildSeasonOptions = (history: RiderHistoryItem[]): SeasonOption[] => {
	const seasonMap = new Map<string, SeasonOption>();

	history.forEach((item) => {
		const seasonKey = getSeasonKey(item);
		const resultDate = new Date(item.event_date || item.event?.date || "");
		const resultDateValue = Number.isNaN(resultDate.getTime())
			? 0
			: resultDate.getTime();

		let label = item.season?.name?.trim() || "";
		if (!label && seasonKey.startsWith("year-")) {
			label = seasonKey.replace("year-", "");
		}
		if (!label) {
			label = "Unassigned";
		}

		const existing = seasonMap.get(seasonKey);
		if (!existing || resultDateValue > existing.lastResultDate) {
			seasonMap.set(seasonKey, {
				key: seasonKey,
				label,
				lastResultDate: resultDateValue,
			});
		}
	});

	return Array.from(seasonMap.values()).sort(
		(a, b) => b.lastResultDate - a.lastResultDate,
	);
};

export const getSeasonHistory = (
	history: RiderHistoryItem[],
	selectedSeasonKey: string,
) => {
	if (!selectedSeasonKey) {
		return history;
	}

	return history.filter((historyItem) => getSeasonKey(historyItem) === selectedSeasonKey);
};
